import React, { useState } from 'react';
import type { WalletDetail } from '../types';

// ─────────────────────────────────────────────────────────
// 點數錢包圖表視圖（純 SVG 折線，未引入外部圖表 library）
// ─────────────────────────────────────────────────────────
type RangeKey = '7d' | '30d' | '90d' | 'all';

const RANGE_OPTIONS: { key: RangeKey; label: string; days: number | null }[] = [
  { key: '7d',  label: '近 7 天',  days: 7  },
  { key: '30d', label: '近 30 天', days: 30 },
  { key: '90d', label: '近 90 天', days: 90 },
  { key: 'all', label: '全部',     days: null },
];

// 線條配色循環
const LINE_PALETTE = [
  '#3b82f6', '#f97316', '#10b981', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#eab308', '#06b6d4', '#a855f7',
];

const NO_REASON_KEY = '（無說明）';
const ALL_SERIES_KEY = '全部';
const ALL_SERIES_COLOR = '#3b82f6';

const dayKey = (iso: string) => iso.slice(0, 10); // YYYY-MM-DD

const UserWalletChart: React.FC<{
  wallets: WalletDetail[];
  /** 由父層在切換進圖表時以事件處理中產生，避免在 render 中呼叫 Date.now() */
  nowMs: number;
}> = ({ wallets, nowMs }) => {
  const [range, setRange] = useState<RangeKey>('30d');
  // 選中的「說明」分類；空 set 視為「全部」（單一條總線）
  const [activeReasons, setActiveReasons] = useState<Set<string>>(new Set());

  const toggleReason = (r: string) => {
    setActiveReasons(prev => {
      const next = new Set(prev);
      if (next.has(r)) next.delete(r); else next.add(r);
      return next;
    });
  };

  // 攤平所有 pointLogs（僅扣點 delta<0）
  const allDeductLogs = wallets.flatMap(w => w.pointLogs).filter(l => l.delta < 0);

  // 時間過濾
  const days = RANGE_OPTIONS.find(r => r.key === range)?.days ?? null;
  const cutoffMs = days != null ? nowMs - days * 86400_000 : 0;
  const timeFilteredLogs = allDeductLogs.filter(
    l => days == null || new Date(l.createdAt).getTime() >= cutoffMs
  );

  // 取目前時間區間內出現過的「說明」清單（distinct + 排序），作為 chip 來源
  const distinctReasons = Array.from(
    new Set(timeFilteredLogs.map(l => l.reason || NO_REASON_KEY))
  ).sort();

  // 依 reason 套用過濾；empty set → 不過濾
  const reasonFilteredLogs = activeReasons.size === 0
    ? timeFilteredLogs
    : timeFilteredLogs.filter(l => activeReasons.has(l.reason || NO_REASON_KEY));

  // 決定每條 series 的鍵：
  //  - 空 set → 單一 series "全部"
  //  - 有選 → 每個被選的 reason 一條
  const seriesKeys: string[] = activeReasons.size === 0
    ? [ALL_SERIES_KEY]
    : Array.from(activeReasons).sort();

  // 取得某 series key 對應的顏色
  const colorOf = (k: string) =>
    activeReasons.size === 0
      ? ALL_SERIES_COLOR
      : LINE_PALETTE[distinctReasons.indexOf(k) % LINE_PALETTE.length];

  // 建立日期軸
  const dayList: string[] = [];
  if (days != null) {
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(nowMs - i * 86400_000);
      dayList.push(d.toISOString().slice(0, 10));
    }
  } else {
    const set = new Set<string>();
    reasonFilteredLogs.forEach(l => set.add(dayKey(l.createdAt)));
    Array.from(set).sort().forEach(k => dayList.push(k));
  }

  // series → day → 累計扣點
  const seriesMap = new Map<string, Map<string, number>>();
  seriesKeys.forEach(k => seriesMap.set(k, new Map(dayList.map(d => [d, 0]))));
  reasonFilteredLogs.forEach(l => {
    const day = dayKey(l.createdAt);
    const seriesKey = activeReasons.size === 0
      ? ALL_SERIES_KEY
      : (l.reason || NO_REASON_KEY);
    const dayMap = seriesMap.get(seriesKey);
    if (!dayMap) return;
    if (!dayMap.has(day)) dayMap.set(day, 0);
    dayMap.set(day, dayMap.get(day)! + -l.delta);
  });

  // 摘要：總扣點（套用所有過濾）
  const totalUsed = reasonFilteredLogs.reduce((s, l) => s + -l.delta, 0);

  // 圖表尺寸
  const W = 720, H = 240, PAD_L = 40, PAD_B = 28, PAD_T = 12, PAD_R = 12;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const maxValue = Math.max(
    1,
    ...Array.from(seriesMap.values()).flatMap(m => Array.from(m.values()))
  );

  const xAt = (i: number) =>
    dayList.length === 1
      ? PAD_L + innerW / 2
      : PAD_L + (i / (dayList.length - 1)) * innerW;
  const yAt = (v: number) => PAD_T + innerH - (v / maxValue) * innerH;

  return (
    <div className="space-y-4">
      {/* 篩選列 */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="flex gap-1">
          {RANGE_OPTIONS.map(r => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              className={`px-2.5 py-1 rounded text-xs border ${
                range === r.key
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-400'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* 說明 chip 列 */}
      <div>
        <div className="text-[11px] text-gray-400 mb-1.5">
          說明過濾（未選 = 顯示全部加總成一條線；可複選同時呈現多條折線）
        </div>
        <div className="flex flex-wrap gap-1.5">
          {distinctReasons.length === 0 ? (
            <span className="text-xs text-gray-400">此區間內無資料</span>
          ) : distinctReasons.map((r, i) => {
            const on = activeReasons.has(r);
            const color = LINE_PALETTE[i % LINE_PALETTE.length];
            return (
              <button
                key={r}
                type="button"
                onClick={() => toggleReason(r)}
                className={`px-2.5 py-1 rounded-full text-xs border flex items-center gap-1.5 ${
                  on
                    ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-gray-700/40'
                    : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500'
                }`}
              >
                <span
                  className="w-2 h-2 rounded-full inline-block"
                  style={{ backgroundColor: on ? color : '#9ca3af' }}
                />
                {r}
              </button>
            );
          })}
        </div>
      </div>

      {/* 摘要卡片：總扣點 + 各說明分項（僅在有選說明時顯示分項） */}
      <div className="flex flex-wrap gap-2">
        <div className="rounded-lg border border-gray-100 dark:border-gray-700 px-4 py-2 min-w-[120px]">
          <div className="text-[11px] text-gray-400">總扣點</div>
          <div className="text-xl font-semibold text-orange-600 dark:text-orange-400">{totalUsed}</div>
        </div>
        {activeReasons.size > 0 && seriesKeys.map(k => {
          const dayMap = seriesMap.get(k)!;
          const used = Array.from(dayMap.values()).reduce((s, v) => s + v, 0);
          return (
            <div
              key={k}
              className="rounded-lg border border-gray-100 dark:border-gray-700 px-4 py-2 min-w-[120px]"
            >
              <div className="text-[11px] text-gray-400 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: colorOf(k) }} />
                <span className="truncate max-w-[160px]" title={k}>{k}</span>
              </div>
              <div className="text-xl font-semibold text-gray-700 dark:text-gray-200">{used}</div>
            </div>
          );
        })}
      </div>

      {/* 主圖：折線 */}
      <div className="rounded-lg border border-gray-100 dark:border-gray-700 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-gray-500 dark:text-gray-400">每日扣點趨勢</div>
          {/* 圖例 */}
          <div className="flex flex-wrap gap-3">
            {seriesKeys.map(k => (
              <div key={k} className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                <span className="w-3 h-0.5 rounded" style={{ backgroundColor: colorOf(k) }} />
                {k}
              </div>
            ))}
          </div>
        </div>
        {dayList.length === 0 ? (
          <p className="text-xs text-gray-400 py-6 text-center">此區間內沒有資料</p>
        ) : (
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
            {/* Y 軸刻度 */}
            {[0, 0.25, 0.5, 0.75, 1].map(p => {
              const y = PAD_T + innerH * (1 - p);
              return (
                <g key={p}>
                  <line x1={PAD_L} x2={W - PAD_R} y1={y} y2={y} stroke="currentColor" strokeOpacity={0.08} />
                  <text x={PAD_L - 4} y={y + 3} textAnchor="end" fontSize="9" fill="currentColor" fillOpacity={0.5}>
                    {Math.round(maxValue * p)}
                  </text>
                </g>
              );
            })}
            {/* 折線 */}
            {seriesKeys.map(k => {
              const dayMap = seriesMap.get(k)!;
              const points = dayList.map((d, i) => `${xAt(i)},${yAt(dayMap.get(d) ?? 0)}`).join(' ');
              const color = colorOf(k);
              return (
                <g key={k}>
                  <polyline
                    points={points}
                    fill="none"
                    stroke={color}
                    strokeWidth={1.6}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                  {dayList.map((d, i) => {
                    const v = dayMap.get(d) ?? 0;
                    if (v === 0) return null;
                    return (
                      <circle
                        key={d}
                        cx={xAt(i)}
                        cy={yAt(v)}
                        r={2}
                        fill={color}
                      >
                        <title>{`${d}  ${k}: ${v} 點`}</title>
                      </circle>
                    );
                  })}
                </g>
              );
            })}
            {/* X 軸標籤（稀疏） */}
            {dayList.map((k, i) => {
              const step = Math.max(1, Math.ceil(dayList.length / 8));
              if (i % step !== 0 && i !== dayList.length - 1) return null;
              return (
                <text
                  key={k}
                  x={xAt(i)}
                  y={H - PAD_B + 14}
                  textAnchor="middle"
                  fontSize="9"
                  fill="currentColor"
                  fillOpacity={0.5}
                >
                  {k.slice(5)}
                </text>
              );
            })}
          </svg>
        )}
      </div>
    </div>
  );
};

export default UserWalletChart;
